import React from 'react';
import { InputMaybe, RepaymentOption } from 'src/graphql';
import { DataListItem } from '@brighte/form-components';
import { useRebateAmount } from '@hooks';
import { useApplicationData } from '@provider';
import { LABELS, formatAddress, formatDollars, isJointApplicant, productIds } from '@utils';
import {
  buildWhatYourePurchasing,
  getBrightePayReviewSections,
  getDisclaimers,
  getFeesChargeDetails,
  getFinanceDetails,
  getPurchaseDetails,
  getRepaymentDetails,
  getRepaymentDueField,
  getTermLabel,
  getVendorDetails,
  withSingleRepaymentTerm,
} from './utils';

const { INSTALLATION_ADDRESS } = LABELS;
const formatOptions = {
  hideZeroCents: true,
  zeroValue: '$0.00',
};

function useFinanceSummary(): Array<{ title: string; items: DataListItem[] | [] }> | [] {
  const isJoint = isJointApplicant();

  const { quote, application, Finance: finance } = useApplicationData(['quote', 'application', 'Finance']);
  const subsidy = useRebateAmount({ financialProductId: quote?.financialProduct?.id, quote });
  let repaymentTerm = finance?.repaymentTerm;
  let isCashApplication = finance?.payment?.isCashApplication;
  let address = finance?.address;

  //If it is joint, we use the primary applicant's details to get finance summary.
  if (isJoint) {
    repaymentTerm = application?.repaymentTerm?.termInMonths;
    isCashApplication = application?.payment?.isCashApplication;
    address = application?.address;
  }
  if (!repaymentTerm || !quote || !quote.productConfiguration) return [];

  const {
    financialProduct: { id: financialProductId },
    repaymentOptions,
    relatedReferrals,
  } = quote;

  const repayment = getRepaymentDetails(repaymentTerm, quote);
  const vendorDetails = getVendorDetails(quote);
  const isBrightePayForm = financialProductId === productIds.BRIGHTE_PAY;

  const financeFooter = !isJoint
    ? getDisclaimers({
        quote,
        selectedTerm: repaymentTerm,
        isCashApplication: isCashApplication,
        applicationSubStatus: application?.subStatus,
      }).map(({ symbol, message }) => {
        return {
          // to ensure both string and ReactNode types are handled and formatted correctly
          label:
            typeof message === 'string'
              ? `${symbol}${'  '}${message}`
              : React.createElement('span', null, [symbol, ' ', message]),
          value: '',
        };
      })
    : [];

  const financePurchaseDetails = getFinanceDetails({
    isCashApplication,
    repaymentTerm,
    subsidy,
    quote,
  });

  const financeTitle = ({
    termInMonths,
    repaymentOptions,
    repaymentAmount,
  }: {
    termInMonths?: InputMaybe<number>;
    repaymentOptions?: RepaymentOption[] | null;
    repaymentAmount?: InputMaybe<number>;
  }) =>
    repaymentOptions && termInMonths && repaymentAmount
      ? `${getTermLabel(repaymentOptions, termInMonths)} from ${formatDollars(repaymentAmount)} per fortnight`
      : '';

  const getFeesCharge = () => {
    switch (financialProductId) {
      case productIds.FLEX:
        return [getRepaymentDueField(quote)];
      case productIds.BRIGHTE_PAY:
        return financePurchaseDetails;
      default:
        return [];
    }
  };

  //Multi-category summary
  if (relatedReferrals?.length && quote.productConfiguration?.supportMultipleCategories) {
    const multiCategoryQuotes = [quote, ...relatedReferrals];
    const totalRepaymentForAllCategories = isCashApplication
      ? 0
      : multiCategoryQuotes.reduce((acc, q) => {
          const repaymentAmount = getRepaymentDetails(repaymentTerm, q)?.amount ?? 0;
          return acc + repaymentAmount;
        }, 0);

    if (isCashApplication) {
      return [
        {
          title: '',
          items: financePurchaseDetails,
        },
        ...multiCategoryQuotes.map(multiCategoryQuote => ({
          title: multiCategoryQuote.category.name,
          items: [
            {
              label: 'Vendor',
              value: multiCategoryQuote.vendor.tradingName,
            },
            {
              label: 'Amount',
              value: formatDollars(multiCategoryQuote.financedAmount, formatOptions),
            },
          ],
        })),
      ];
    }

    return [
      {
        title: financeTitle({
          termInMonths: repayment?.termInMonths,
          repaymentAmount: totalRepaymentForAllCategories,
          repaymentOptions,
        }),
        items: [],
      },
      {
        title: '',
        items: financePurchaseDetails,
      },
      ...multiCategoryQuotes.map(multiCategoryQuote => ({
        title: multiCategoryQuote.category.name,
        items: [
          {
            label: 'Vendor',
            value: multiCategoryQuote.vendor.tradingName,
          },
          {
            label: 'Repayments',
            value: `from ${formatDollars(getRepaymentDetails(repaymentTerm, multiCategoryQuote)?.amount ?? 0)} per fortnight`,
          },
        ],
      })),
      { title: '', items: financeFooter },
    ];
  }

  //Single category summary
  const purchase = getPurchaseDetails(quote);
  const installationAddress = {
    title: '',
    items: [{ label: INSTALLATION_ADDRESS, value: address && formatAddress(address) }],
  };

  // Non-Brighte-Pay products keep their existing finance content; only the shared sections are restructured.
  // Quote details and installer contact rows stay Brighte Pay only — other products keep the
  // heading-only vendor block they render today.
  if (!isBrightePayForm) {
    return [
      buildWhatYourePurchasing(purchase),
      {
        title: !withSingleRepaymentTerm(repaymentOptions)
          ? financeTitle({
              termInMonths: repayment?.termInMonths,
              repaymentAmount: repayment?.amount,
              repaymentOptions,
            })
          : `${quote.financialProduct.name}`,
        items: [...getFeesCharge()],
      },
      installationAddress,
      { title: vendorDetails.title ?? '', items: [] },
      { title: '', items: financeFooter },
    ];
  }

  // Brighte Pay: the full restructured review, shared with the active finance step so both stay identical.
  // showFeesFootnote is left off: this summary renders no disclaimer footer for Brighte Pay, so a "2"
  // marker on the Fees & charges heading would have nothing to refer to.
  return [
    ...getBrightePayReviewSections({
      financeDetails: financePurchaseDetails,
      feesDetails: getFeesChargeDetails({ quote }),
      purchaseDetails: purchase,
      vendorDetails,
      productName: quote.financialProduct.name,
    }),
    installationAddress,
  ];
}

export { useFinanceSummary };
